// src/components/Portal.tsx
'use client'

import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'

interface PortalProps {
  children: React.ReactNode
  zIndex?: number
}

const MOUNT_ID = 'meui-cookies-portal'

/**
 * Robust Portal component with proper mounting and cleanup
 * Creates isolated container in body with high z-index
 */
export function Portal({ children, zIndex = 999997 }: PortalProps) {
  const [mounted, setMounted] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Create or get portal container
    let container = document.getElementById(MOUNT_ID) as HTMLDivElement | null

    if (!container) {
      container = document.createElement('div')
      container.id = MOUNT_ID
      container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: ${zIndex};
        isolation: isolate;
      `
      document.body.appendChild(container)
    }

    containerRef.current = container
    setMounted(true)

    return () => {
      // Don't remove container on unmount - keep it for future use
      // Only cleanup on page unload
      setMounted(false)
    }
  }, [zIndex])

  if (!mounted || !containerRef.current) {
    return null
  }

  return createPortal(children, containerRef.current)
}
